import {
  ArrowDownIcon,
  CaretLeftIcon,
  HeadsetIcon,
  PackageIcon,
  PlugsIcon,
  ShieldCheckIcon,
  SignOutIcon,
  TimerIcon,
  XCircleIcon,
} from "@phosphor-icons/react";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import AnimatedModal from "../../components/AnimatedModal";
import PricingSkeleton from '../../components/SkeletonLoaders/PricingSkeleton';
import {
  useAuth,
  useActivateTrial,
  useChangePlan,
  useConfirmPlanChange,
  useInitiateSubscription,
  useStripePlans,
  useTrackEvent,
} from "../../hooks";
import { openRazorpaySubscriptionCheckout } from "../../services/razorpayService";
import { IChangePlanPreviewResponse } from "../../interfaces";
import { Spinner } from "flowbite-react";
import { cn, formatCurrency } from "../../lib/utils";
import { useTheme } from "../../providers/ThemeProvider";
import { CurrencyCode } from "../../shared/constants";
import PricingCard from "./components/PricingCard";
import { getCardTheme } from "./interface/pricingMeta";
import ThemeToggle from "../../components/ThemeToggle";


const Pricing: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const { user, logout, subscriptionDetails: subDetails } = useAuth();
  const { isDark } = useTheme();
  const [isAnnual, setIsAnnual] = useState(false);

  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [pendingChangePlanId, setPendingChangePlanId] = useState<number | null>(null);
  const [previewData, setPreviewData] = useState<IChangePlanPreviewResponse | null>(null);
  const [showDowngradeModal, setShowDowngradeModal] = useState(false);

  useEffect(() => {
    if (searchParams.get("payment_cancelled") === "true") {
      toast.error("Payment was cancelled. No charge has been made.");
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete("payment_cancelled");
        return next;
      }, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // True for any user who has ever had a plan or trial (cancelled users may have plan=null)
  const hasEverSubscribed =
    !!user?.plan ||
    user?.subscription_status === "canceled" ||
    subDetails?.has_used_trial === true;

  const hasActivePlan =
    user?.subscription_status === "active" ||
    user?.subscription_status === "trialing";

  const { trackEvent } = useTrackEvent();

  const { data: plansData, isLoading: isLoadingPlans } = useStripePlans();
  const initiateMutation = useInitiateSubscription();
  const changePlanMutation = useChangePlan();
  const confirmChangeMutation = useConfirmPlanChange();
  const activateTrialMutation = useActivateTrial();
  const [trialError, setTrialError] = useState<string | null>(null);

  const isLoading = isLoadingPlans;
  const canStartTrial = !subDetails?.plan && !subDetails?.has_used_trial;

  const handleStartTrial = () => {
    setTrialError(null);
    activateTrialMutation.mutate(undefined, {
      onSuccess: () => {
        localStorage.setItem("workspace_setup_pending", "true");
        window.location.href = "/workspace-setup";
      },
      onError: (err: Error) => {
        setTrialError(
          err?.message ||
          t("pricing.trialError", "Failed to activate trial. Please try again."),
        );
      },
    });
  };

  const activePlanId = subDetails?.plan?.id ?? null;

  const plans = (plansData?.results?.filter((p) => p.is_active && p.interval === (isAnnual ? "year" : "month")) ?? []).map(
    (p) => ({
      ...p,
      is_current_plan: activePlanId !== null ? p.id === activePlanId : p.is_current_plan,
    })
  );


  const trialDays =
    plansData?.results?.find((p) => {
      const isEnterprise = !!(p.package ? p.package.is_enterprise : p.is_enterprise);
      return !isEnterprise && p.trial_days > 0;
    })?.trial_days ?? 7;

  const trustItems = [
    { key: "trust_no_sdk", label: "Zero SDK · OAuth 2.0 only", Icon: ShieldCheckIcon },
    { key: "trust_connect", label: "Connect in under 5 minutes", Icon: PlugsIcon },
    { key: "trust_trial", label: `${trialDays}-day trial · charged on Day ${trialDays + 1}`, Icon: TimerIcon },
    { key: "trust_cancel", label: `Cancel anytime before Day ${trialDays + 1}`, Icon: XCircleIcon },
  ];

  const handleLogout = () => {
    logout();
  };

  const handleSubscribe = (planId: number) => {
    const selectedPlan = plans.find((p) => p.id === planId);

    trackEvent("button_clicked", {
      button_id: "subscribe_plan",
      plan_id: planId,
      plan_name: selectedPlan?.name ?? null,
      billing_interval: isAnnual ? "annual" : "monthly",
      action: hasActivePlan ? "change_plan" : "new_subscription",
    });

    if (hasActivePlan) {
      const currentAmount = parseFloat(subDetails?.plan?.amount ?? "0");
      const selectedAmount = parseFloat(selectedPlan?.amount ?? "0");

      if (currentAmount > 0 && selectedAmount > 0 && selectedAmount < currentAmount) {
        setShowDowngradeModal(true);
        trackEvent("modal_opened", {
          modal_id: "downgrade_info",
          from_plan: subDetails?.plan?.name ?? null,
          to_plan: selectedPlan?.name ?? null,
        });
        return;
      }

      // Open the modal immediately — it shows a spinner until previewData arrives
      setPendingChangePlanId(planId);

      changePlanMutation.mutate(
        { plan_id: planId },
        {
          onSuccess: (res) => {
            setPreviewData(res);
            trackEvent("modal_opened", {
              modal_id: "plan_change_confirm",
              plan_id: planId,
              plan_name: selectedPlan?.name ?? null,
              charge_amount: res.charge_amount,
            });
          },
          onError: (err: unknown) => {
            // Close the modal and surface the error
            setPendingChangePlanId(null);
            setPreviewData(null);

            const error = err as { status?: number; message?: string };
            if (error?.status === 402) {
              toast.error("Payment method update required. Please check your billing settings.");
              navigate("/billing");
              return;
            }
            toast.error(error?.message || "Failed to fetch plan preview");
            trackEvent("error_displayed", {
              error_context: "plan_change_preview",
              error_message: error?.message ?? "Failed to fetch plan preview",
            });
          },
        }
      );
      return;
    }
    initiateMutation.mutate(
      {
        plan_id: planId,
        success_url: `${window.location.origin}/payment/verify?session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: `${window.location.origin}/pricing?payment_cancelled=true`,
      },
      {
        onSuccess: (res) => {
          if (res.provider === "razorpay") {
            openRazorpaySubscriptionCheckout({
              key: res.provider_payload.key_id,
              subscription_id: res.provider_payload.subscription_id,
              name: t("common.appName", "AppRevBooster"),
              description: t("common.paymentDescription", "Subscription"),
              prefill: {
                email: user?.email ?? undefined,
                name: user?.name ?? undefined,
              },
              theme: { color: "#3399cc" },
              handler: (response) => {
                navigate(
                  `/payment/verify?razorpay_payment_id=${response.razorpay_payment_id}&razorpay_subscription_id=${response.razorpay_subscription_id}&razorpay_signature=${response.razorpay_signature}`
                );
              },
              modal: {
                ondismiss: () => {
                  toast.error(t("payment.cancelled", "Payment was cancelled. No charge has been made."));
                },
              },
            });
          } else {
            if (res.checkout_url) window.location.href = res.checkout_url;
          }
        },
        onError: (err: Error) => {
          toast.error(err?.message || "Failed to initiate checkout");
          trackEvent("error_displayed", {
            error_context: "checkout_initiate",
            error_message: err?.message ?? "Failed to initiate checkout",
          });
        },
      }
    );
  };

  const handleConfirmChangePlan = () => {
    if (!pendingChangePlanId) return;
    confirmChangeMutation.mutate(
      { plan_id: pendingChangePlanId },
      {
        onSuccess: (res) => {
          toast.success(res.message || "Plan changed successfully");
          trackEvent("form_submitted", {
            form_id: "confirm_plan_change",
            plan_id: pendingChangePlanId,
            result: "success",
          });
          setPendingChangePlanId(null);
          setPreviewData(null);
          navigate("/billing");
        },
        onError: (err: Error) => {
          toast.error(err?.message || "Failed to change plan");
          trackEvent("error_displayed", {
            error_context: "confirm_plan_change",
            error_message: err?.message ?? "Failed to change plan",
          });
        },
      }
    );
  };

  if (isLoading) return <PricingSkeleton />;

  return (
    <div className={cn(
      "relative min-h-[100dvh] overflow-hidden",
      isDark
        ? "bg-[linear-gradient(160deg,#111111_0%,#1a1a1a_40%,#141414_70%,#1a1a1a_100%)]"
        : "bg-[linear-gradient(160deg,#eef2fc_0%,#f5f7fd_40%,#edf1fb_70%,#f2f5fd_100%)]",
    )}>
      {/* ── Atmospheric blobs ── */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none z-0"
        style={{
          background: isDark ? `
            radial-gradient(ellipse 800px 560px at -8% -8%,  rgba(48,205,115,0.04)  0%, transparent 52%),
            radial-gradient(ellipse 700px 560px at 108% 8%,  rgba(255,255,255,0.02) 0%, transparent 52%),
            radial-gradient(ellipse 560px 440px at 52% 110%, rgba(48,205,115,0.03)  0%, transparent 52%),
            radial-gradient(ellipse 340px 260px at 80% 52%,  rgba(255,255,255,0.01) 0%, transparent 50%)
          ` : `
            radial-gradient(ellipse 800px 560px at -8% -8%,  rgba(26,61,124,0.12)  0%, transparent 52%),
            radial-gradient(ellipse 700px 560px at 108% 8%,  rgba(42,91,168,0.09)  0%, transparent 52%),
            radial-gradient(ellipse 560px 440px at 52% 110%, rgba(48,205,115,0.08) 0%, transparent 52%),
            radial-gradient(ellipse 340px 260px at 80% 52%,  rgba(26,61,124,0.05)  0%, transparent 50%)
          `,
        }}
      />
      <div
        aria-hidden
        className="fixed inset-0 h-[100dvh] pointer-events-none z-0 bg-[length:52px_52px]"
        style={{
          backgroundImage: isDark ? `
            linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)
          ` : `
            linear-gradient(rgba(26,61,124,0.035) 1px, transparent 1px),
            linear-gradient(90deg, rgba(26,61,124,0.035) 1px, transparent 1px)
          `,
        }}
      />

      <div className={cn("relative z-[1]", plans.length === 0 && "min-h-[100dvh] flex flex-col justify-center")}>
        {/* ── Back button for users who have ever subscribed; logout-only for new users ── */}
        {hasEverSubscribed ? (
          <>
            <div className="fixed top-4 left-5 z-10">
              <button
                onClick={() => navigate("/billing")}
                className="inline-flex items-center gap-[6px] px-4 py-2 rounded-lg border border-[rgba(26,61,124,0.2)] bg-[rgba(255,255,255,0.85)] backdrop-blur-[6px] text-[#1a3d7c] text-[13px] font-semibold cursor-pointer transition-colors duration-150 hover:bg-white dark:bg-neutral-800/80 dark:border-indigo-800/40 dark:text-indigo-300 dark:hover:bg-neutral-700/80"
              >
                <CaretLeftIcon size={14} weight="bold" />
                Back to Billing
              </button>
            </div>
            <div className="fixed top-4 right-5 z-10">
              <ThemeToggle />
            </div>
          </>
        ) : (
          <div className="fixed top-4 right-5 z-10 flex items-center gap-3">
            <ThemeToggle />
            {/* User info chip */}
            <div className="flex items-center gap-2 px-3 py-[6px] rounded-lg bg-[rgba(255,255,255,0.85)] backdrop-blur-[6px] border border-[rgba(26,61,124,0.15)] shadow-[0_1px_6px_rgba(26,61,124,0.08)] dark:bg-neutral-800/80 dark:border-neutral-700/50 dark:shadow-none">
              {user?.profile_picture && typeof user.profile_picture === 'string' ? (
                <img
                  src={user.profile_picture}
                  alt={user.name ?? user.email}
                  className="w-8 h-8 rounded-full object-cover shrink-0 border border-[rgba(26,61,124,0.12)] dark:border-neutral-600/50"
                />
              ) : (
                <div className="w-8 h-8 rounded-full bg-[rgba(26,61,124,0.12)] flex items-center justify-center shrink-0 dark:bg-indigo-900/40">
                  <span className="text-[#1a3d7c] text-[13px] font-bold uppercase select-none dark:text-indigo-300">
                    {(user?.name ?? user?.email ?? '?')[0]}
                  </span>
                </div>
              )}
              <div className="flex flex-col leading-tight">
                {user?.name && (
                  <span className="text-[12px] font-semibold text-[#0f172a] max-w-[140px] truncate dark:text-white">{user.name}</span>
                )}
                <span className="text-[11px] text-[#64748b] max-w-[140px] truncate dark:text-gray-400">{user?.email}</span>
              </div>
            </div>
            <button
              onClick={() => setShowLogoutModal(true)}
              className="inline-flex items-center gap-[6px] px-4 py-2 rounded-lg border border-red-200 bg-[rgba(255,255,255,0.85)] backdrop-blur-[6px] text-red-600 text-[13px] font-semibold cursor-pointer transition-colors duration-150 hover:bg-red-50 dark:bg-neutral-800/80 dark:border-red-800/50 dark:hover:bg-red-950/30"
            >
              Logout
              <SignOutIcon size={16} weight="bold" />
            </button>
          </div>
        )}

        <AnimatedModal
          isConfirmation
          show={showLogoutModal}
          onClose={() => setShowLogoutModal(false)}
          onConfirm={handleLogout}
          title={t("common.confirm_logout")}
          children={
            <div className="text-gray-700 dark:text-gray-300 text-center">
              {t("common.logout_confirmation_message")}
            </div>
          }
          confirmText={t("common.logout")}
          confirmButtonColor="red"
        />

        <AnimatedModal
          isConfirmation
          show={showDowngradeModal}
          onClose={() => {
            setShowDowngradeModal(false);
            trackEvent("modal_closed", { modal_id: "downgrade_info", dismissed: true });
          }}
          onConfirm={() => {
            setShowDowngradeModal(false);
            trackEvent("button_clicked", { button_id: "downgrade_create_ticket" });
            navigate("/support?tab=tickets");
          }}
          title={
            <span className="flex items-center gap-2">
              <ArrowDownIcon size={18} weight="bold" className="text-amber-500" />
              Downgrade Requires a Support Ticket
            </span>
          }
          confirmText="Create Support Ticket"
          confirmButtonColor="indigo"
          cancelText="Cancel"
        >
          <div className="flex flex-col gap-4 text-sm text-gray-600 dark:text-gray-400 py-1">
            <p>
              To keep your data and billing accurate, plan downgrades are processed manually by our support team — usually within one business day.
            </p>
            <ul className="space-y-2 text-[13px]">
              <li className="flex items-start gap-2">
                <span className="mt-[3px] w-[6px] h-[6px] rounded-full bg-indigo-500 shrink-0" />
                Your current plan stays active until the support team confirms the change.
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-[3px] w-[6px] h-[6px] rounded-full bg-indigo-500 shrink-0" />
                Any unused credit for the remaining billing period will be prorated.
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-[3px] w-[6px] h-[6px] rounded-full bg-indigo-500 shrink-0" />
                Features above your new plan's limit will be disabled after the change takes effect.
              </li>
            </ul>
            <p className="flex items-start gap-2 text-xs text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-neutral-800 rounded-lg px-3 py-2 border border-gray-100 dark:border-neutral-700">
              <HeadsetIcon size={14} className="mt-[1px] shrink-0 text-indigo-400" />
              Click "Create Support Ticket" and our team will reach out to complete the downgrade for you.
            </p>
          </div>
        </AnimatedModal>

        <AnimatedModal
          isConfirmation={!!previewData}
          show={pendingChangePlanId !== null}
          onClose={() => {
            setPendingChangePlanId(null);
            setPreviewData(null);
          }}
          onConfirm={handleConfirmChangePlan}
          title="Confirm Plan Change"
          children={
            <div className="text-gray-700 dark:text-gray-300 text-center flex flex-col gap-3">
              {previewData ? (
                <>
                  {previewData.action === "upgrade" && previewData.proration_breakdown ? (
                    <>
                      <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                        Here's how today's charge is calculated:
                      </p>
                      <div className="text-left rounded-xl border border-gray-100 dark:border-neutral-700 bg-gray-50 dark:bg-neutral-800 overflow-hidden">
                        <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 dark:border-neutral-700">
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            New plan charge
                            <span className="ml-1 text-gray-400 dark:text-gray-500">
                              ({previewData.proration_breakdown.remaining_days}/{previewData.proration_breakdown.billing_period_days} days)
                            </span>
                          </span>
                          <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                            {formatCurrency(parseFloat(previewData.proration_breakdown.new_plan_charge), (previewData.currency as CurrencyCode) ?? "USD")}
                          </span>
                        </div>
                        <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 dark:border-neutral-700">
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            Current plan credit
                          </span>
                          <span className="text-xs font-semibold text-green-600">
                            -{formatCurrency(parseFloat(previewData.proration_breakdown.current_plan_credit), (previewData.currency as CurrencyCode) ?? "USD")}
                          </span>
                        </div>
                        <div className="flex items-center justify-between px-4 py-3 bg-indigo-50 dark:bg-indigo-500/10 dark:border-t dark:border-indigo-800/30">
                          <span className="text-sm font-bold text-indigo-800 dark:text-indigo-300">
                            Today's charge
                          </span>
                          <span className="text-sm font-bold text-indigo-700 dark:text-indigo-300">
                            {formatCurrency(parseFloat(previewData.proration_breakdown.payable_amount), (previewData.currency as CurrencyCode) ?? "USD")}
                          </span>
                        </div>
                      </div>
                      <p className="text-[10px] text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 p-2 rounded-lg border border-amber-100 dark:border-amber-800/40">
                        Future payments will be adjusted according to the new plan rate.
                      </p>
                    </>
                  ) : (
                    <p className="text-sm font-medium">
                      Your plan will change to the new plan at the end of your current billing cycle.
                    </p>
                  )}
                </>
              ) : (
                <div className="py-6 flex flex-col items-center gap-3">
                  <Spinner size="md" color="info" />
                  <p className="text-xs text-gray-400">
                    Calculating proration...
                  </p>
                </div>
              )}
            </div>
          }
          confirmText={
            confirmChangeMutation.isPending ? "Loading…" : "Confirm Change"
          }
          confirmButtonColor="blue"
        />

        {/* ══════════════ HERO ══════════════ */}
        {plans.length > 0 && (
          <section className="animate-[p-fade-in_0.55s_cubic-bezier(0.4,0,0.2,1)_both] py-[52px] px-6 pb-[44px] text-center">
            <div className="max-w-[620px] mx-auto">
              {/* Pill badge */}
              <div className="flex justify-center mb-[22px]">
                <span className="inline-flex items-center gap-2 px-[18px] py-[6px] rounded-full text-[11px] font-bold tracking-[0.07em] uppercase bg-[rgba(255,255,255,0.85)] text-[#1a3d7c] border-[1.5px] border-[rgba(26,61,124,0.22)] shadow-[0_2px_12px_rgba(26,61,124,0.10)] dark:bg-neutral-800/70 dark:text-indigo-300 dark:border-indigo-700/40 dark:shadow-none">
                  <span className="w-[7px] h-[7px] rounded-full bg-[#1a3d7c] shrink-0 animate-[p-ring_2.4s_ease-in-out_infinite]" />
                  Transparent pricing · Scale-based, never feature-gated
                </span>
              </div>

              {/* Headline */}
              <h1 className="font-sans text-[clamp(28px,5.2vw,52px)] font-black leading-[1.05] tracking-[-0.03em] mb-[18px]">
                <span className="text-[#0f172a] dark:text-white">
                  Your apps earn.
                </span>
                <br />
                <span className="inline-block bg-[linear-gradient(118deg,#1a3d7c_0%,#2a5ba8_50%,#30cd73_100%)] bg-clip-text text-transparent">
                  Know exactly how much.
                </span>
              </h1>

              <p className="text-[15px] text-[#475569] leading-[1.72] max-w-[480px] mx-auto mb-[30px] font-normal dark:text-gray-400">
                One dashboard. Every revenue stream. Every ad network. Your true profit margin — finally visible.
              </p>

              {/* Billing toggle */}
              <div className="flex flex-col items-center gap-[9px]">
                <div className="inline-flex items-center gap-[11px] bg-[rgba(255,255,255,0.90)] border-[1.5px] border-[rgba(26,61,124,0.18)] rounded-full px-[22px] py-2 shadow-[0_4px_18px_rgba(26,61,124,0.10)] dark:bg-neutral-800/90 dark:border-neutral-700/40 dark:shadow-none">
                  <span
                    className={cn(
                      "text-[13.5px] font-bold transition-colors [transition-duration:220ms] select-none",
                      isAnnual ? "text-[#94a3b8] dark:text-gray-600" : "text-[#0f172a] dark:text-white",
                    )}
                  >
                    Monthly
                  </span>

                  <div
                    role="switch"
                    aria-checked={isAnnual}
                    onClick={() => {
                      trackEvent("button_clicked", {
                        button_id: "billing_interval_toggle",
                        toggled_to: isAnnual ? "monthly" : "annual",
                      });
                      setIsAnnual((v) => !v);
                    }}
                    className={cn(
                      "w-12 h-[27px] rounded-full border-[1.5px] cursor-pointer relative shrink-0 transition-[background,border-color] [transition-duration:260ms]",
                      isAnnual
                        ? "bg-[linear-gradient(135deg,#1a3d7c,#2a5ba8)] border-[#1a3d7c]"
                        : "bg-[rgba(26,61,124,0.18)] border-[rgba(26,61,124,0.35)]",
                    )}
                  >
                    <div
                      className={cn(
                        "w-[21px] h-[21px] bg-white rounded-full absolute top-[2px] left-[2px] transition-transform [transition-duration:280ms] cubic-bezier(0.4,0,0.2,1) shadow-[0_1px_6px_rgba(26,61,124,0.22)]",
                        isAnnual ? "translate-x-[21px]" : "translate-x-0",
                      )}
                    />
                  </div>

                  <span
                    className={cn(
                      "text-[13.5px] font-bold transition-colors [transition-duration:220ms] select-none",
                      isAnnual ? "text-[#0f172a] dark:text-white" : "text-[#94a3b8] dark:text-gray-600",
                    )}
                  >
                    Annual
                  </span>

                  <span className="inline-flex items-center px-[10px] py-[3px] rounded-full text-[10px] font-bold tracking-[0.06em] uppercase bg-[#f0fdf4] text-[#15803d] border border-[#86efac]">
                    Save 20%
                  </span>
                </div>

                <p className="text-[11.5px] text-[#64748b] font-normal dark:text-gray-500">
                  Switch to annual billing and lock in a full year at 20% off
                </p>
              </div>
            </div>
          </section>
        )}

        {/* ══════════════ CARDS ══════════════ */}
        <div className={cn(
          "animate-[p-fadein_0.65s_0.10s_cubic-bezier(0.4,0,0.2,1)_both] px-5 pb-12 flex justify-center",
          plans.length === 0 && "flex-1 flex-col items-center justify-center w-full"
        )}>
          {plans.length > 0 ? (
            <div
              className={cn(
                "grid gap-6 items-start justify-items-stretch w-full",
                plans.length === 1 && "max-w-[420px] grid-cols-1",
                plans.length === 2 &&
                "max-w-[860px] grid-cols-1 sm:grid-cols-2",
                plans.length === 3 &&
                "max-w-[1100px] grid-cols-1 md:grid-cols-3",
                plans.length >= 4 &&
                "max-w-[1320px] grid-cols-1 sm:grid-cols-2 lg:grid-cols-4",
              )}
            >
              {plans.map((plan, idx) => (
                <PricingCard
                  key={plan.id}
                  plan={plan}
                  theme={getCardTheme(plan, idx)}
                  isAnnual={isAnnual}
                  canStartTrial={canStartTrial}
                  onSubscribe={handleSubscribe}
                  isPending={initiateMutation.isPending || changePlanMutation.isPending}
                  pendingPlanId={changePlanMutation.variables?.plan_id ?? initiateMutation.variables?.plan_id ?? null}
                  hasActivePlan={hasActivePlan}
                  currentPlanAmount={subDetails?.plan ? parseFloat(subDetails.plan.amount) : null}
                />
              ))}
            </div>
          ) : (
            <div
              className="relative w-full max-w-[560px] overflow-hidden rounded-[28px] p-[1.5px] shadow-[0_8px_48px_rgba(26,61,124,0.18)]"
              style={{ background: "linear-gradient(135deg,#1a3d7c 0%,#2a5ba8 50%,#30cd73 100%)" }}
            >
              <div className={cn(
                "relative rounded-[27px] backdrop-blur-[12px] px-10 py-14 flex flex-col items-center gap-5 overflow-hidden",
                isDark ? "bg-neutral-900/95" : "bg-[rgba(255,255,255,0.92)]",
              )}>
                <div aria-hidden className="absolute -top-10 -left-10 w-48 h-48 rounded-full opacity-20 pointer-events-none"
                  style={{ background: "radial-gradient(circle,#2a5ba8,transparent 70%)" }} />
                <div aria-hidden className="absolute -bottom-10 -right-10 w-48 h-48 rounded-full opacity-20 pointer-events-none"
                  style={{ background: "radial-gradient(circle,#30cd73,transparent 70%)" }} />

                <div className="relative flex items-center justify-center w-[72px] h-[72px] rounded-full"
                  style={{ background: "linear-gradient(135deg,rgba(26,61,124,0.12),rgba(48,205,115,0.12))" }}
                >
                  <PackageIcon size={34} weight="duotone" color="#2a5ba8" className="block" />
                </div>

                <div className="text-center">
                  <p className="text-[22px] font-black tracking-[-0.02em] leading-tight mb-[6px]"
                    style={{
                      background: "linear-gradient(118deg,#1a3d7c 0%,#2a5ba8 50%,#30cd73 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    {t("pricing.comingSoon", "Pricing Coming Soon")}
                  </p>
                </div>

                <div className="w-16 h-[2px] rounded-full"
                  style={{ background: "linear-gradient(90deg,#1a3d7c,#30cd73)" }} />

                <p className="text-[12px] text-[#475569] dark:text-gray-400 font-medium text-center -mb-1">
                  {t("pricing.trialNudge", "Get full access — free for 90 days, no card required")}
                </p>

                {trialError && (
                  <p className="text-red-500 text-[13px] font-medium text-center px-2 -my-1">
                    {trialError}
                  </p>
                )}

                <button
                  onClick={handleStartTrial}
                  disabled={activateTrialMutation.isPending || !canStartTrial}
                  className="relative inline-flex items-center gap-2 px-8 py-[13px] rounded-full text-white text-[14.5px] font-bold tracking-[0.01em] shadow-[0_4px_20px_rgba(26,61,124,0.30)] transition-opacity hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed"
                  style={{ background: "linear-gradient(118deg,#1a3d7c 0%,#2a5ba8 55%,#30cd73 100%)" }}
                >
                  {activateTrialMutation.isPending ? (
                    <>
                      <svg className="animate-spin w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                      </svg>
                      {t("pricing.activatingTrial", "Activating…")}
                    </>
                  ) : (
                    t("pricing.startTrial", "Start 90 Day Trial")
                  )}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ══════════════ TRUST BAR ══════════════ */}
        {plans.length > 0 && (
          <div className="animate-[p-fadein_0.55s_0.22s_cubic-bezier(0.4,0,0.2,1)_both] px-6 pb-[60px] flex justify-center">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 w-full max-w-[1000px]">
              {trustItems.map(({ key, label, Icon }) => (
                <div
                  key={key}
                  className="flex items-center gap-[10px] px-4 py-[13px] bg-[rgba(255,255,255,0.75)] border border-[rgba(26,61,124,0.12)] rounded-[14px] shadow-[0_1px_8px_rgba(26,61,124,0.06)] backdrop-blur-[6px] dark:bg-neutral-800/60 dark:border-neutral-700/30 dark:shadow-none"
                >
                  <Icon
                    size={18}
                    color={isDark ? "#818cf8" : "#1a3d7c"}
                    weight="duotone"
                    className="shrink-0"
                  />
                  <span className="text-[12px] font-medium text-[#1e293b] leading-[1.4] dark:text-gray-300">
                    {label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <style>{`
        @keyframes p-ring {
          0%   { box-shadow: 0 0 0 0   rgba(26,61,124,0.55); }
          70%  { box-shadow: 0 0 0 9px rgba(26,61,124,0);    }
          100% { box-shadow: 0 0 0 0   rgba(26,61,124,0);    }
        }
        @keyframes p-fadein {
          from { opacity:0; transform:translateY(16px); }
          to   { opacity:1; transform:translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default Pricing;
